/**
 * Provider Analytics Page
 * Shows performance metrics for provider's published content
 */

/* eslint-disable max-lines */

import { authenticatedFetch, type ApiProviderAnalyticsResponse } from "@lumi/data";
import { BarChart3, Clock, Eye, TrendingUp, Users } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";
import {
  AnalyticsStatCard,
  LineChartCard,
  MultiLineChartCard,
  TimeRangeSelector,
  type TimeRange,
} from "../../../shared/components/charts";
import { LoadingSpinner } from "../../../shared/components/loading/LoadingSpinner";
import { cn } from "../../../shared/components/ui/cn";
import { useCascadeFadeIn } from "../../../shared/hooks/useCascadeFadeIn";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

interface TitleMetric {
  titleId: string;
  name: string;
  posterUrl?: string | null;
  views: number;
  uniqueViewers: number;
  watchTimeMinutes: number;
  completionRate: number;
}

interface TitleTrend {
  titleId: string;
  name: string;
  data: Array<{ date: string; views: number; watchTime: number }>;
}

interface AnalyticsData {
  overview: {
    totalViews: number;
    uniqueViewers: number;
    totalWatchTimeMinutes: number;
    avgCompletionRate: number;
  };
  titles: TitleMetric[];
  trends: {
    daily: Array<{ date: string; views: number; watchTime: number }>;
    byTitle: TitleTrend[];
  };
}

/**
 * Format minutes to a human-readable string
 */
function formatWatchTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}m`;
}

/**
 * Get color class for completion rate
 */
function getCompletionRateColor(rate: number): string {
  if (rate >= 70) return "text-green-500";
  if (rate >= 40) return "text-yellow-500";
  return "text-neutral-400";
}

/**
 * Title Performance Row Component
 */
interface TitleRowProps {
  readonly title: TitleMetric;
  readonly maxViews: number;
  readonly index: number;
}

function TitlePerformanceRow({ title, maxViews, index }: TitleRowProps) {
  const { ref, isVisible } = useCascadeFadeIn<HTMLTableRowElement>({
    index,
    threshold: 0.1,
    staggerDelay: 50,
  });

  const barWidth = maxViews > 0 ? (title.views / maxViews) * 100 : 0;

  return (
    <tr
      ref={ref}
      className={cn(
        "border-b border-neutral-800 bg-transparent hover:bg-neutral-800/30 transition-all duration-500",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
      )}
    >
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          {title.posterUrl ? (
            <img src={title.posterUrl} alt={title.name} className="w-10 h-14 object-cover rounded" />
          ) : (
            <div className="w-10 h-14 bg-none rounded flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-neutral-600" />
            </div>
          )}
          <span className="text-white font-medium truncate max-w-[200px]">{title.name}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <div className="w-24 h-2 bg-none rounded-full overflow-hidden">
            <div
              className="h-full bg-lumi-accent rounded-full transition-all duration-500"
              style={{ width: `${barWidth}%` }}
            />
          </div>
          <span className="text-white font-medium w-16 text-right">{title.views.toLocaleString()}</span>
        </div>
      </td>
      <td className="py-4 px-4 text-neutral-400">{title.uniqueViewers.toLocaleString()}</td>
      <td className="py-4 px-4 text-neutral-400">{formatWatchTime(title.watchTimeMinutes)}</td>
      <td className="py-4 px-4">
        <span className={cn("font-medium", getCompletionRateColor(title.completionRate))}>{title.completionRate}%</span>
      </td>
    </tr>
  );
}

/**
 * Empty state when no analytics data is available
 */
function EmptyState(): ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <BarChart3 className="w-16 h-16 text-neutral-600 mb-4" />
      <h2 className="text-xl font-semibold text-white mb-2">No analytics data yet</h2>
      <p className="text-neutral-400 max-w-md">
        Once your content is published and viewed, you'll see performance metrics here.
      </p>
    </div>
  );
}

/**
 * Overview stats grid component
 */
interface OverviewStatsProps {
  readonly overview: AnalyticsData["overview"];
}

function OverviewStats({ overview }: OverviewStatsProps): ReactElement {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <AnalyticsStatCard
        label="Total Views"
        value={overview.totalViews.toLocaleString()}
        subtitle="Play starts across all titles"
        icon={<Eye className="w-5 h-5" />}
      />
      <AnalyticsStatCard
        label="Unique Viewers"
        value={overview.uniqueViewers.toLocaleString()}
        subtitle="Individual viewers"
        icon={<Users className="w-5 h-5" />}
      />
      <AnalyticsStatCard
        label="Watch Time"
        value={formatWatchTime(overview.totalWatchTimeMinutes)}
        subtitle="Total time watched"
        icon={<Clock className="w-5 h-5" />}
      />
      <AnalyticsStatCard
        label="Avg Completion"
        value={`${overview.avgCompletionRate}%`}
        subtitle="Average watch percentage"
        icon={<TrendingUp className="w-5 h-5" />}
      />
    </div>
  );
}

/**
 * Title performance table component
 */
interface TitlePerformanceTableProps {
  readonly titles: TitleMetric[];
  readonly maxViews: number;
}

function TitlePerformanceTable({ titles, maxViews }: TitlePerformanceTableProps): ReactElement {
  return (
    <div className="bg-none border border-neutral-800 rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-neutral-800">
        <h2 className="text-lg font-semibold text-white">Title Performance</h2>
        <p className="text-sm text-neutral-400">Ranked by total views</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-800 text-left text-sm text-neutral-500">
              <th className="py-3 px-4 font-medium">Title</th>
              <th className="py-3 px-4 font-medium">Views</th>
              <th className="py-3 px-4 font-medium">Unique Viewers</th>
              <th className="py-3 px-4 font-medium">Watch Time</th>
              <th className="py-3 px-4 font-medium">Completion</th>
            </tr>
          </thead>
          <tbody>
            {titles.map((title, index) => (
              <TitlePerformanceRow key={title.titleId} title={title} maxViews={maxViews} index={index} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ProviderAnalyticsPage(): ReactElement {
  const { accessToken, refreshAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");

  useEffect(() => {
    async function fetchAnalytics() {
      if (!accessToken) return;

      setLoading(true);
      setError(null);

      try {
        const response = await authenticatedFetch<ApiProviderAnalyticsResponse>(
          `${API_BASE}/v1/provider/analytics?range=${timeRange}`,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setData(response);
      } catch (err) {
        console.error("Failed to fetch provider analytics:", err);
        setError(err instanceof Error ? err.message : "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    }

    void fetchAnalytics();
  }, [accessToken, refreshAuth, timeRange]);

  // Transform trend data for the chart
  const chartData =
    data?.trends.daily.map((d) => ({
      name: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      value: d.views,
    })) ?? [];

  const maxViews = data?.titles.reduce((max, t) => Math.max(max, t.views), 0) ?? 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <BarChart3 className="w-12 h-12 text-neutral-600 mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">Unable to load analytics</h2>
        <p className="text-neutral-400 max-w-md">{error}</p>
      </div>
    );
  }

  const hasData = data && (data.overview.totalViews > 0 || data.titles.length > 0);

  return (
    <div className="space-y-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Content Performance</h1>
          <p className="text-sm text-neutral-400 pb-6">Track how your published content is performing</p>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>

      {hasData ? (
        <>
          <OverviewStats overview={data.overview} />

          {/* Views Trend Chart - Multi-line if multiple titles */}
          {data.trends.byTitle && data.trends.byTitle.length > 1 ? (
            <MultiLineChartCard
              series={data.trends.byTitle}
              title="Views Over Time by Title"
              description="Daily view counts for your top titles"
              valueKey="views"
              footerMetric="Total"
              footerValue={data.overview.totalViews}
            />
          ) : (
            chartData.length > 0 && (
              <LineChartCard
                data={chartData}
                title="Views Over Time"
                description="Daily view counts for your content"
                valueKey="value"
                valueLabel="Views"
                nameKey="name"
                footerMetric="Total"
                footerValue={data.overview.totalViews}
              />
            )
          )}

          {/* Title Performance Table */}
          {data.titles.length > 0 && <TitlePerformanceTable titles={data.titles} maxViews={maxViews} />}
        </>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
